<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold">Transcript Templates</h3>
        <p class="text-sm text-gray-500">Customize the layout and appearance of generated transcripts.</p>
      </div>
      <Button
        v-if="canCreate"
        icon="pi pi-plus"
        label="New Template"
        size="small"
        data-testid="transcript-template-new"
        @click="openCreate"
      />
    </div>

    <div class="bg-white rounded-lg shadow">
      <DataTable
        size="small"
        :value="templates"
        :loading="loading"
        stripedRows
        responsiveLayout="scroll"
        dataKey="id"
        :paginator="templates.length > 10"
        :rows="10"
      >
        <template #empty>
          <div class="text-center py-6 text-gray-400 text-sm">
            No transcript templates yet. Create one to customize your transcript layout.
          </div>
        </template>

        <Column field="name" header="Name">
          <template #body="{ data }">
            <div class="text-sm font-medium">{{ data.name }}</div>
          </template>
        </Column>
        <Column header="Style">
          <template #body="{ data }">
            <Tag :value="data.style_code" :severity="styleSeverity(data.style_code)" />
          </template>
        </Column>
        <Column header="Assigned Programmes">
          <template #body="{ data }">
            <div v-if="data.programmes?.length" class="flex flex-wrap gap-1">
              <Tag
                v-for="p in data.programmes"
                :key="p.id"
                :value="`${p.program_name}${p.degree ? ` (${p.degree})` : ''}`"
                severity="secondary"
                class="text-xs"
              />
            </div>
            <span v-else class="text-xs text-gray-400 italic">Not assigned</span>
          </template>
        </Column>
        <Column header="Created">
          <template #body="{ data }">
            <span class="text-sm text-gray-500">{{ formatDateTime(data.created_at) }}</span>
          </template>
        </Column>
        <Column header="Actions" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center gap-1">
              <Button
                v-if="canUpdate"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                text
                rounded
                v-tooltip.top="'Edit'"
                :data-testid="`transcript-template-edit-${data.id}`"
                @click="openEdit(data)"
              />
              <Button
                v-if="canUpdate"
                icon="pi pi-link"
                size="small"
                severity="info"
                text
                rounded
                v-tooltip.top="'Assign to Programme'"
                :data-testid="`transcript-template-assign-${data.id}`"
                @click="assigningTemplate = data"
              />
              <Button
                v-if="canDelete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                rounded
                v-tooltip.top="'Delete'"
                :data-testid="`transcript-template-delete-${data.id}`"
                @click="onDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <TranscriptTemplateForm
      v-model:visible="formOpen"
      :template="editingTemplate"
      :designOptions="designOptions"
      :loading="creating || updating"
      @save="onSave"
    />

    <AssignTranscriptTemplateToProgramme
      :template="assigningTemplate"
      @close="assigningTemplate = null"
      @assigned="assigningTemplate = null"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useTranscriptTemplates } from '@/composables/transcript/useTranscriptTemplates';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { formatDateTime } from '@/utils';
import type { TranscriptTemplate } from '@/types/composition-functions';
import TranscriptTemplateForm from './TranscriptTemplateForm.vue';
import AssignTranscriptTemplateToProgramme from './AssignTranscriptTemplateToProgramme.vue';

const {
  templates,
  loading,
  designOptions,
  creating,
  updating,
  createTemplate,
  updateTemplate,
  confirmDelete,
} = useTranscriptTemplates();

const { currentUser } = storeToRefs(useUserStore());

const canCreate = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Create, AppPermissions.Transcripts.Resource));
const canUpdate = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Update, AppPermissions.Transcripts.Resource));
const canDelete = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Delete, AppPermissions.Transcripts.Resource));

const formOpen = ref(false);
const editingTemplate = ref<TranscriptTemplate | null>(null);
const assigningTemplate = ref<TranscriptTemplate | null>(null);

const styleSeverity = (code: string) => {
  switch (code) {
  case 'classic': return 'info';
  case 'modern': return 'success';
  case 'minimal': return 'secondary';
  default: return 'info';
  }
};

const openCreate = () => {
  editingTemplate.value = null;
  formOpen.value = true;
};

const openEdit = (template: TranscriptTemplate) => {
  editingTemplate.value = template;
  formOpen.value = true;
};

const onDelete = (template: TranscriptTemplate) => {
  confirmDelete({ id: template.id, name: template.name });
};

const onSave = async(data: { name: string; style_code: string; properties: Record<string, unknown> }) => {
  if (creating.value || updating.value) return;

  if (editingTemplate.value) {
    await updateTemplate({
      id: editingTemplate.value.id,
      name: data.name,
      style_code: data.style_code,
      properties: data.properties,
    });
  } else {
    await createTemplate({
      name: data.name,
      style_code: data.style_code,
      properties: data.properties,
    });
  }
  formOpen.value = false;
};
</script>
